import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { isAxiosError } from 'axios';
import { Alert, Box, Button, Card, CardContent, Chip, Stack, Typography } from '@mui/material';
import GoogleIcon from '@mui/icons-material/CalendarMonthOutlined';
import { disconnectGoogleCalendar, getGoogleCalendarStatus, getGoogleConnectUrl } from '../../api/googleIntegration';

/** Self-service "connect your own Google Calendar" page (Phase 77) — the
 * natural home for this and any future personal integration settings,
 * reachable from the Sidebar's user menu next to "Change password".
 * Google Calendar sync is otherwise fully dormant (GOOGLE_CALENDAR_ENABLED=
 * false) until real OAuth credentials are configured — the Connect button
 * surfaces that plainly instead of failing silently. */
export const MyAccountPage = () => {
  const queryClient = useQueryClient();
  const [searchParams, setSearchParams] = useSearchParams();
  const [connectError, setConnectError] = useState<string | null>(null);
  // Captured once on mount — the effect below clears `connected` from the
  // URL almost immediately, so re-deriving this from `searchParams` on
  // every render would flip it back to false before the banner ever paints.
  const [redirectResult] = useState(() => searchParams.get('connected'));

  const statusQuery = useQuery({ queryKey: ['google-calendar-status'], queryFn: getGoogleCalendarStatus });

  useEffect(() => {
    if (searchParams.has('connected')) {
      queryClient.invalidateQueries({ queryKey: ['google-calendar-status'] });
      setSearchParams((params) => {
        params.delete('connected');
        return params;
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const connectMutation = useMutation({
    mutationFn: getGoogleConnectUrl,
    onSuccess: (url) => {
      window.location.href = url;
    },
    onError: (err) => {
      setConnectError(
        isAxiosError(err) && err.response?.status === 400
          ? 'Google Calendar integration is not configured yet. Ask your administrator to set it up.'
          : 'Something went wrong. Please try again.',
      );
    },
  });

  const disconnectMutation = useMutation({
    mutationFn: disconnectGoogleCalendar,
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['google-calendar-status'] }),
  });

  const justConnected = redirectResult === '1';
  const connectFailed = redirectResult === '0';

  return (
    <Box sx={{ p: 3, maxWidth: 480 }}>
      <Typography variant="h5" sx={{ mb: 3, fontWeight: 700 }}>
        My Account
      </Typography>

      <Card variant="outlined">
        <CardContent sx={{ p: 4 }}>
          <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mb: 2 }}>
            <GoogleIcon color="action" />
            <Typography variant="h6">Google Calendar</Typography>
          </Stack>

          {justConnected && (
            <Alert severity="success" sx={{ mb: 2 }}>
              Google Calendar connected.
            </Alert>
          )}
          {connectFailed && (
            <Alert severity="error" sx={{ mb: 2 }}>
              Couldn&apos;t connect Google Calendar. Please try again.
            </Alert>
          )}
          {connectError && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {connectError}
            </Alert>
          )}

          {statusQuery.isLoading ? null : statusQuery.data?.connected ? (
            <Stack spacing={2}>
              <Stack direction="row" spacing={1} alignItems="center">
                <Chip size="small" color="success" label="Connected" />
                <Typography variant="body2" color="text.secondary">
                  {statusQuery.data.email}
                </Typography>
              </Stack>
              <Typography variant="body2" color="text.secondary">
                Personal Appointments you create will be added to this calendar, with assigned counselors invited
                as attendees.
              </Typography>
              <Button
                variant="outlined"
                color="error"
                disabled={disconnectMutation.isPending}
                onClick={() => disconnectMutation.mutate()}
                sx={{ alignSelf: 'flex-start' }}
              >
                Disconnect
              </Button>
            </Stack>
          ) : (
            <Stack spacing={2}>
              <Stack direction="row" spacing={1} alignItems="center">
                <Chip size="small" label="Not connected" />
              </Stack>
              <Typography variant="body2" color="text.secondary">
                Connect your Google account so Personal Appointments you create are automatically added to your
                Google Calendar.
              </Typography>
              <Button
                variant="contained"
                disabled={connectMutation.isPending || statusQuery.isLoading}
                onClick={() => connectMutation.mutate()}
                sx={{ alignSelf: 'flex-start' }}
              >
                Connect Google Calendar
              </Button>
            </Stack>
          )}
        </CardContent>
      </Card>
    </Box>
  );
};
